/**
 * Захват экрана без getDisplayMedia — для мобильных браузеров и WebView приложения Casper.
 * Видимая часть страницы рисуется в canvas (modern-screenshot: DOM → SVG foreignObject → canvas).
 * Скриншот — один такой кадр, запись — серия кадров через canvas.captureStream + микрофон.
 * Элементы с атрибутом data-capture-exclude (плашка записи) в кадр не попадают.
 */
import type { Context } from "modern-screenshot"
import { MAX_RECORDING_SEC, pickMimeType, screenBitrate, screenCaptureSupported, type RecorderHandle, type RecordingResult } from "./recorder"
import { fixWebmDuration } from "./webm-duration"
import { drawInteractionMarks, trackInteractions, type InteractionTracker } from "./interaction-marks"

export const CAPTURE_EXCLUDE_ATTR = "data-capture-exclude"

/** Пауза между кадрами записи: DOM-снимок тяжёлый, чаще 4 кадров/с телефон не успевает */
const FRAME_INTERVAL_MS = 250
const RECORD_MAX_SIDE = 1920
const RECORD_MAX_SCALE = 2

type Shot = typeof import("modern-screenshot")

const loadShot = (): Promise<Shot> => import("modern-screenshot")

export function domCaptureSupported(): boolean {
  return typeof document !== "undefined" && typeof HTMLCanvasElement !== "undefined"
}

export function domRecordingSupported(): boolean {
  return domCaptureSupported() && typeof MediaRecorder !== "undefined" && typeof HTMLCanvasElement.prototype.captureStream === "function"
}

export type CaptureMode = "display" | "dom"

/** display — системный выбор вкладки/окна/экрана (компьютер), dom — снимок страницы (телефон, WebView) */
export function captureMode(): CaptureMode | null {
  if (screenCaptureSupported()) return "display"
  return domCaptureSupported() ? "dom" : null
}

const SKIP_TAGS = new Set(["NOSCRIPT", "TEMPLATE", "SCRIPT", "STYLE", "LINK"])

const keep = (node: Node) => !(node instanceof Element && (SKIP_TAGS.has(node.tagName) || node.hasAttribute(CAPTURE_EXCLUDE_ATTR) || node.hasAttribute("data-capture-ignore")))

function background(): string {
  const bg = getComputedStyle(document.body).backgroundColor
  return bg && bg !== "rgba(0, 0, 0, 0)" && bg !== "transparent" ? bg : "#ffffff"
}

function options(scale: number) {
  return {
    width: window.innerWidth,
    height: window.innerHeight,
    scale,
    backgroundColor: background(),
    // без явного margin клон body получает отступ 8px из UA-стилей внутри foreignObject
    style: { margin: "0" },
    filter: keep,
    timeout: 8000,
    features: { restoreScrollPosition: true },
  }
}

const stamp = () => new Date().toISOString().slice(0, 19).replace(/[T:]/g, "-")

/** Снимок видимой части страницы */
export async function captureDomScreenshot(): Promise<File> {
  const shot = await loadShot()
  let canvas: HTMLCanvasElement
  try {
    canvas = await shot.domToCanvas(document.body, options(Math.min(2, window.devicePixelRatio || 1)))
  } catch (e) {
    throw new Error(`Не удалось сделать снимок: ${(e as Error)?.message || "неизвестная ошибка"}`)
  }
  const blob = await new Promise<Blob | null>((r) => canvas.toBlob(r, "image/png"))
  if (!blob) throw new Error("Не удалось сохранить снимок")
  return new File([blob], `screenshot-${stamp()}.png`, { type: "image/png" })
}

/** Запись страницы: кадры DOM в canvas, касания, клики и прокрутка отмечаются поверх кадра, голос — с микрофона */
export async function startDomRecording(opts: { mic: boolean; onDone: (r: RecordingResult) => void; onError: (message: string) => void }): Promise<RecorderHandle> {
  if (!domRecordingSupported()) throw new Error("Браузер не умеет записывать экран")
  const shot = await loadShot()

  const out = document.createElement("canvas")
  const g = out.getContext("2d")
  if (!g) throw new Error("Браузер не умеет записывать экран")

  let viewport = { w: 0, h: 0 }
  let scale = 1
  let context: Context<HTMLElement> | null = null
  const ensureContext = async () => {
    const w = window.innerWidth
    const h = window.innerHeight
    if (context && w === viewport.w && h === viewport.h) return context
    if (context) shot.destroyContext(context)
    viewport = { w, h }
    scale = Math.max(1, Math.min(RECORD_MAX_SCALE, window.devicePixelRatio || 1, RECORD_MAX_SIDE / Math.max(w, h, 1)))
    out.width = Math.round(w * scale) & ~1
    out.height = Math.round(h * scale) & ~1
    context = await shot.createContext(document.body, options(scale))
    return context
  }

  let tracker: InteractionTracker | null = null

  const drawFrame = async () => {
    const frame = await shot.domToCanvas(await ensureContext())
    g.imageSmoothingQuality = "high"
    g.drawImage(frame, 0, 0, out.width, out.height)
    if (tracker) drawInteractionMarks(g, tracker.marks(), scale)
  }

  try {
    await drawFrame()
  } catch (e) {
    if (context) shot.destroyContext(context)
    throw new Error(`Не удалось начать запись экрана: ${(e as Error)?.message || "неизвестная ошибка"}`)
  }

  let mic: MediaStream | null = null
  let micError: string | null = null
  if (opts.mic && navigator.mediaDevices?.getUserMedia) {
    try {
      mic = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } })
    } catch (e) {
      const name = (e as { name?: string })?.name
      micError = name === "NotAllowedError" ? "доступ к микрофону запрещён" : name === "NotFoundError" ? "микрофон не найден" : (e as Error)?.message || "неизвестная ошибка"
    }
  } else if (opts.mic) {
    micError = "браузер не даёт доступ к микрофону"
  }
  const micTracks = mic?.getAudioTracks() ?? []
  const video = out.captureStream(Math.round(1000 / FRAME_INTERVAL_MS))
  const stream = new MediaStream([...video.getVideoTracks(), ...micTracks])

  const mime = pickMimeType()
  let recorder: MediaRecorder
  try {
    recorder = new MediaRecorder(stream, { ...(mime ? { mimeType: mime } : {}), videoBitsPerSecond: screenBitrate(out.width, out.height), audioBitsPerSecond: 64_000 })
  } catch (e) {
    mic?.getTracks().forEach((t) => t.stop())
    if (context) shot.destroyContext(context)
    throw new Error(`Не удалось начать запись экрана: ${(e as Error)?.message || "неизвестная ошибка"}`)
  }

  const chunks: Blob[] = []
  const startedAt = Date.now()
  let pausedMs = 0
  let pausedAt: number | null = null
  let cancelled = false
  let finished = false
  let running = true

  const release = () => {
    running = false
    tracker?.stop()
    tracker = null
    for (const t of [...video.getTracks(), ...(mic?.getTracks() ?? [])]) t.stop()
    clearTimeout(limit)
    if (context) shot.destroyContext(context)
    context = null
  }

  const loop = async () => {
    let failures = 0
    while (running) {
      const t0 = Date.now()
      if (recorder.state === "recording" && !document.documentElement.hasAttribute("data-capture-hold")) {
        try {
          await drawFrame()
          failures = 0
        } catch (e) {
          if (++failures >= 5 && running) {
            cancelled = true
            release()
            try {
              recorder.stop()
            } catch {
              // уже остановлен
            }
            opts.onError(`Запись прервалась: ${(e as Error)?.message || "не удалось снять кадр"}`)
            return
          }
        }
      }
      await new Promise((r) => setTimeout(r, Math.max(30, FRAME_INTERVAL_MS - (Date.now() - t0))))
    }
  }

  recorder.ondataavailable = (e) => e.data.size && chunks.push(e.data)
  recorder.onerror = (e) => {
    release()
    opts.onError(`Запись прервалась: ${(e as unknown as { error?: Error }).error?.message || "неизвестная ошибка"}`)
  }
  recorder.onstop = () => {
    release()
    if (cancelled || finished) return
    finished = true
    const type = recorder.mimeType || mime || "video/webm"
    const ext = type.includes("mp4") ? "mp4" : "webm"
    const raw = new Blob(chunks, { type: type.split(";")[0] })
    const durationMs = Date.now() - startedAt - pausedMs - (pausedAt ? Date.now() - pausedAt : 0)
    void (ext === "webm" ? fixWebmDuration(raw, durationMs) : Promise.resolve(raw)).then((blob) =>
      opts.onDone({ file: new File([blob], `screen-recording-${stamp()}.${ext}`, { type: raw.type }), durationSec: Math.round(durationMs / 1000), hasMic: micTracks.length > 0, hasSystemAudio: false }),
    )
  }
  const limit = setTimeout(() => recorder.state !== "inactive" && recorder.stop(), MAX_RECORDING_SEC * 1000)
  tracker = trackInteractions()
  recorder.start(1000)
  void loop()

  return {
    stop: () => recorder.state !== "inactive" && recorder.stop(),
    cancel: () => {
      cancelled = true
      if (recorder.state !== "inactive") recorder.stop()
      else release()
    },
    pause: () => {
      if (recorder.state !== "recording") return
      recorder.pause()
      pausedAt = Date.now()
    },
    resume: () => {
      if (recorder.state !== "paused") return
      recorder.resume()
      if (pausedAt) pausedMs += Date.now() - pausedAt
      pausedAt = null
    },
    get paused() {
      return recorder.state === "paused"
    },
    startedAt,
    hasMic: micTracks.length > 0,
    hasSystemAudio: false,
    micError,
    mode: "dom",
    displaySurface: null,
  }
}